import { useCallback, useEffect, useState } from "react";
import { Loader2, Users, ArrowRight, XCircle, RotateCcw, ShieldCheck } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "../../../lib/api";
import { formatDateTime, formatUserName } from "./ui";


const API_PATH = "/projects";

const KIND_META = {
    assignment:    { label: "Assignment",    tone: "text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200",    Icon: ShieldCheck },
    reassignment:  { label: "Reassignment",  tone: "text-amber-700 bg-amber-50 ring-1 ring-amber-200",         Icon: RotateCcw },
    transfer:      { label: "Transfer",      tone: "text-sky-700 bg-sky-50 ring-1 ring-sky-200",               Icon: ArrowRight },
    revoke:        { label: "Revoke",        tone: "text-slate-700 bg-slate-100 ring-1 ring-slate-300",        Icon: XCircle },
};


function fmtWhen(iso) {
    return formatDateTime(iso);
}


function labelForUser(snapshot, fallbackId) {
    return formatUserName(snapshot, fallbackId);
}


export default function AccountabilityChainPanel({ workItemId }) {
    const [state, setState] = useState({ loading: true, items: [], currentOwner: null, error: null });

    const load = useCallback(async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const r = await api.get(`${API_PATH}/work-item/${workItemId}/accountability-chain`);
            setState({
                loading: false,
                items: r.data.items || [],
                currentOwner: r.data.current_owner_snapshot,
                total: r.data.total || 0,
                error: null,
            });
        } catch (err) {
            const msg = formatApiError(err, "Could not load accountability chain.");
            setState({ loading: false, items: [], currentOwner: null, error: msg });
            toast.error(msg);
        }
    }, [workItemId]);

    useEffect(() => { load(); }, [load]);

    if (state.loading) {
        return (
            <section className="reedos-card p-5" data-testid="acc-chain-panel">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading accountability chain…
                </div>
            </section>
        );
    }

    return (
        <section className="reedos-card p-5 space-y-4" data-testid="acc-chain-panel">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-indigo-50 ring-1 ring-indigo-200">
                        <Users className="w-4 h-4 text-indigo-700" />
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-slate-900" data-testid="acc-chain-heading">
                            Chain of Accountability
                        </h3>
                        <p className="text-xs text-slate-500">
                            Append-only ledger. Every ownership change is recorded here, most-recent first.
                        </p>
                    </div>
                </div>
                <Button
                    variant="ghost" size="sm" onClick={load}
                    data-testid="acc-chain-refresh-btn"
                >
                    Refresh
                </Button>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-600">
                <span className="uppercase tracking-wide text-[10px] text-slate-400">Current Owner</span>
                <span className="font-mono text-slate-800" data-testid="acc-chain-current-owner">
                    {labelForUser(state.currentOwner, state.currentOwner?.user_id)}
                </span>
                <span className="text-slate-300">·</span>
                <span data-testid="acc-chain-total">{state.total || state.items.length} entr{(state.total || state.items.length) === 1 ? "y" : "ies"}</span>
            </div>

            {state.error && (
                <div className="text-sm text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-md p-3" data-testid="acc-chain-error">
                    {state.error}
                </div>
            )}

            {state.items.length === 0 && !state.error && (
                <div className="text-sm text-slate-500 italic" data-testid="acc-chain-empty">
                    No accountability entries yet.
                </div>
            )}

            <ol className="relative space-y-3" data-testid="acc-chain-list">
                {state.items.map((e) => {
                    const meta = KIND_META[e.kind] || {
                        label: e.kind, tone: "text-slate-700 bg-slate-100 ring-1 ring-slate-300", Icon: Users,
                    };
                    const Icon = meta.Icon;
                    return (
                        <li
                            key={e.id}
                            className="flex gap-3 items-start"
                            data-testid={`acc-chain-entry-${e.ordinal}`}
                        >
                            <div className="mt-1 flex-shrink-0 h-6 w-6 rounded-full bg-slate-100 ring-1 ring-slate-300 flex items-center justify-center text-[11px] font-mono text-slate-700">
                                {e.ordinal}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${meta.tone}`}>
                                        <Icon className="w-3 h-3" /> {meta.label}
                                    </span>
                                    <span className="text-xs text-slate-500">{fmtWhen(e.at)}</span>
                                </div>
                                <div className="mt-1 text-sm text-slate-800 flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-slate-600" data-testid={`acc-chain-from-${e.ordinal}`}>
                                        {e.from_user_id ? labelForUser(e.from_user_snapshot, e.from_user_id) : "—"}
                                    </span>
                                    <ArrowRight className="w-3 h-3 text-slate-400" />
                                    <span className="font-mono text-slate-900" data-testid={`acc-chain-to-${e.ordinal}`}>
                                        {labelForUser(e.to_user_snapshot, e.to_user_id)}
                                    </span>
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                    By {labelForUser(e.actor_snapshot, e.actor_user_id)}
                                    {e.reason && (
                                        <>
                                            {" · "}
                                            <span className="italic">{e.reason}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}
